// Leaderboard (plan.md §4.5).
//
// "What's happening near <area>" aggregated over a town-scale radius. The
// area is described as a distance rather than a place name: naming it needs
// reverse geocoding, and Nominatim's usage policy discourages the automated
// volume that would take (plan.md OQ9). "Within 15 km" costs nothing and
// removes a dependency.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iconFor } from '../lib/icons.js';
import { fetchLeaderboard } from '../lib/db.js';
import { relativeTime } from '../lib/format.js';
import { useLocale } from '../lib/i18n.js';
import { localizeCategoryName } from '../lib/taxonomy-i18n.js';

const RADII = [
  { value: 5000, labelKey: 'leaderboard.radius5' },
  { value: 15000, labelKey: 'leaderboard.radius15' },
  { value: 30000, labelKey: 'leaderboard.radius30' },
];

export default function Leaderboard({ centre, daysBack, onPickCategory, onClose }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [rows, setRows] = useState(null);
  const [radiusM, setRadiusM] = useState(15000);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!centre) return undefined;
    let cancelled = false;

    setRows(null);
    setError(null);
    fetchLeaderboard({ lat: centre.lat, lng: centre.lng, radiusM, daysBack })
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((err) => { if (!cancelled) setError(err.message); });

    return () => { cancelled = true; };
  }, [centre, radiusM, daysBack]);

  const max = rows?.length ? Math.max(...rows.map((r) => Number(r.report_count))) : 0;
  const activeRadiusLabel = RADII.find((r) => r.value === radiusM)?.labelKey;

  return (
    <section className="sheet sheet--tall" role="dialog" aria-modal="true" aria-label={t('leaderboard.aria')}>
      <header className="sheet__header">
        <div>
          <h2 className="sheet__title">{t('leaderboard.title')}</h2>
          <p className="sheet__subtitle">
            {t('leaderboard.subtitle', { radius: activeRadiusLabel ? t(activeRadiusLabel) : '', days: t('units.day', { count: daysBack }) })}
          </p>
        </div>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label={t('nav.close')}>×</button>
      </header>

      <div className="segmented" role="group" aria-label={t('leaderboard.areaSize')}>
        {RADII.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`segmented__option${radiusM === option.value ? ' is-active' : ''}`}
            aria-pressed={radiusM === option.value}
            onClick={() => setRadiusM(option.value)}
          >
            {t(option.labelKey)}
          </button>
        ))}
      </div>

      <div className="sheet__body">
        {error && <p className="error" role="alert">{error}</p>}
        {rows === null && !error && <p className="hint">{t('leaderboard.counting')}</p>}

        {rows?.length === 0 && (
          <div className="empty">
            <p className="empty__title">{t('leaderboard.emptyTitle')}</p>
            <p className="empty__body">
              {t('leaderboard.emptyBody')}
            </p>
          </div>
        )}

        {rows?.length > 0 && (
          <ol className="board">
            {rows.map((row, index) => {
              const name = localizeCategoryName(row.category_slug, locale) ?? row.category_name;
              return (
                <li key={row.category_id} className="board__row">
                  <button
                    type="button"
                    className="board__link"
                    onClick={() => onPickCategory({ id: row.category_id, name, slug: row.category_slug })}
                  >
                    <span className="board__rank">{index + 1}</span>
                    <span className="board__icon" aria-hidden="true">
                      {iconFor(row.category_icon, row.collection_slug)}
                    </span>
                    <span className="board__text">
                      <span className="board__name">{name}</span>
                      <span className="board__meta">
                        {t('leaderboard.report', { count: Number(row.report_count) })}
                        {Number(row.corroboration_total) > 0 &&
                          <> · {t('leaderboard.confirmation', { count: Number(row.corroboration_total) })}</>}
                        {' · '}{t('leaderboard.latest', { time: relativeTime(row.last_reported_at) })}
                      </span>
                      <span
                        className="board__bar"
                        style={{ '--fill': `${max ? (Number(row.report_count) / max) * 100 : 0}%` }}
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
